import type { Card, ProductType, ShippingSupply } from "@/context/AppContext";
import {
  type BundleStrategyId,
  type BundleSuggestion,
  availabilityPctOf,
  pickDefaultListingType,
} from "@/features/bundleEngine";
import { buildPricedEconomics } from "@/features/productPricingProfiles";
import { estimateFulfillment } from "@/features/fulfillmentEstimator";
import { computeBuyerValueScore } from "@/features/buyerValueScore";
import type {
  ListingType,
  ProductOpportunity,
} from "@/types/productOpportunity";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function totalQty(cards: Card[]): number {
  return cards.reduce((s, c) => s + (c.quantity ?? 1), 0);
}

function totalMarket(cards: Card[]): number {
  return cards.reduce(
    (s, c) => s + (c.marketPrice ?? 0) * (c.quantity ?? 1),
    0,
  );
}

function totalCostBasis(cards: Card[]): number {
  return cards.reduce((s, c) => s + (c.costBasis ?? 0) * (c.quantity ?? 1), 0);
}

function isTrainer(c: Card): boolean {
  return c.tcgSupertype?.toLowerCase() === "trainer";
}

function isEnergy(c: Card): boolean {
  return c.tcgSupertype?.toLowerCase() === "energy";
}

// ─── Studio strategy generators ───────────────────────────────────────────────

interface StudioStrategyResult {
  strategyId: BundleStrategyId;
  productType: ProductType;
  cards: Card[];
  reasons: string[];
  targetCount: number;
}

function generateRandomValueLot(
  cards: Card[],
  usedIds: Set<string>,
): StudioStrategyResult | null {
  const TARGET = 100;
  const MIN_QTY = 50; // Legacy gate — partial lots below this never surfaced.
  // Take available bulk + commons; cap at 100 cards.
  const pool = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      c.listingStatus === "Unlisted" &&
      c.quantity > 0 &&
      c.condition !== "Damaged",
  );
  if (pool.length === 0) return null;
  const sorted = [...pool].sort(
    (a, b) => (b.marketPrice ?? 0) - (a.marketPrice ?? 0),
  );
  const chosen: Card[] = [];
  let qty = 0;
  for (const c of sorted) {
    if (qty >= TARGET) break;
    if (qty + c.quantity > TARGET) continue;
    chosen.push(c);
    qty += c.quantity;
  }
  if (qty < MIN_QTY) return null;
  return {
    strategyId: "random-value-lot",
    productType: "100-card Random Lot",
    cards: chosen,
    targetCount: TARGET,
    reasons: [
      `Uses ${qty} unlisted cards from current inventory.`,
      "Mix of rares, holos, and commons for buyer appeal.",
    ],
  };
}

function generateTrainerStaplesKit(
  cards: Card[],
  usedIds: Set<string>,
): StudioStrategyResult | null {
  const TARGET = 25;
  const MIN_QTY = 12; // Legacy gate.
  const trainers = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      isTrainer(c) &&
      c.listingStatus === "Unlisted" &&
      c.quantity > 0 &&
      c.condition !== "Damaged",
  );
  if (totalQty(trainers) < MIN_QTY) return null;
  const sorted = [...trainers].sort(
    (a, b) => (b.marketPrice ?? 0) - (a.marketPrice ?? 0),
  );
  return {
    strategyId: "trainer-staples-kit",
    productType: "Trainer Staples Kit",
    cards: sorted.slice(0, TARGET),
    targetCount: TARGET,
    reasons: [
      "Bundles draw, search, and switch trainers for deck-building support.",
      "All unlisted Trainer cards from current inventory.",
    ],
  };
}

function generateEnergyKit(
  cards: Card[],
  usedIds: Set<string>,
): StudioStrategyResult | null {
  const TARGET = 20;
  const MIN_QTY = 20; // Legacy gate — energy kits only fire when fully buildable.
  const energies = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      isEnergy(c) &&
      c.listingStatus === "Unlisted" &&
      c.quantity > 0,
  );
  if (totalQty(energies) < MIN_QTY) return null;
  return {
    strategyId: "energy-kit",
    productType: "Energy Kit",
    cards: energies,
    targetCount: TARGET,
    reasons: [
      "Bundles basic and special Energy cards for deck-building.",
      `Includes ${totalQty(energies)} energy cards.`,
    ],
  };
}

function generateSetBuilderLot(
  cards: Card[],
  usedIds: Set<string>,
): StudioStrategyResult | null {
  const TARGET = 50;
  const MIN_QTY = 25; // Legacy gate.
  const pool = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      c.listingStatus === "Unlisted" &&
      c.quantity > 0 &&
      c.setName &&
      // Set Builder Lots target binder-fillers, which are typically Pokémon
      // cards. Skip trainer/energy-only sets — they're surfaced as their
      // own kits.
      !isTrainer(c) &&
      !isEnergy(c),
  );
  if (pool.length === 0) return null;
  const bySet = new Map<string, Card[]>();
  for (const c of pool) {
    const key = c.setName;
    if (!bySet.has(key)) bySet.set(key, []);
    bySet.get(key)!.push(c);
  }
  const ranked = [...bySet.entries()]
    .map(([setName, setCards]) => ({
      setName,
      setCards,
      qty: totalQty(setCards),
    }))
    .filter((x) => x.qty >= MIN_QTY)
    .sort((a, b) => b.qty - a.qty);
  if (ranked.length === 0) return null;
  const top = ranked[0];
  return {
    strategyId: "set-builder-lot",
    productType: "Set Builder Lot",
    cards: top.setCards.slice(0, TARGET),
    targetCount: TARGET,
    reasons: [
      `Builds a Set Builder Lot from ${top.setName}.`,
      `${top.qty} cards available from this set in unlisted inventory.`,
    ],
  };
}

function generateCharacterTheme(
  cards: Card[],
  usedIds: Set<string>,
): StudioStrategyResult | null {
  const TARGET = 4;
  // Group by Pokémon name. If any name has 4+ unlisted copies across variants,
  // surface a character bundle.
  const pool = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      c.listingStatus === "Unlisted" &&
      c.quantity > 0 &&
      c.tcgSupertype?.toLowerCase().includes("pokémon"),
  );
  if (pool.length === 0) return null;
  const byName = new Map<string, Card[]>();
  for (const c of pool) {
    const key = c.name.split(" ex")[0].split(" V")[0].trim().toLowerCase();
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key)!.push(c);
  }
  // Character bundles are small (target 4); a 1-card "partial" is meaningless,
  // so retain the original 4-card minimum even though that's 100% of target.
  const ranked = [...byName.entries()]
    .map(([name, list]) => ({ name, list, qty: totalQty(list) }))
    .filter((x) => x.qty >= TARGET)
    .sort((a, b) => b.qty - a.qty);
  if (ranked.length === 0) return null;
  const top = ranked[0];
  const display = top.list[0]?.name ?? top.name;
  return {
    strategyId: "character-theme",
    productType: "Character Theme Bundle",
    cards: top.list,
    targetCount: TARGET,
    reasons: [
      `Character bundle built around ${display}.`,
      `${top.qty} unlisted ${display} variants in inventory.`,
    ],
  };
}

// Order matters — earlier strategies claim cards that later strategies can't
// re-use. Specific bundles (energy/trainer/character) run before the
// catch-all set-builder/random lots.
const STUDIO_STRATEGIES = [
  generateEnergyKit,
  generateTrainerStaplesKit,
  generateCharacterTheme,
  generateSetBuilderLot,
  generateRandomValueLot,
];

// ─── Listing-type override per spec ───────────────────────────────────────────

function pickListingType(
  cards: Card[],
  productType: ProductType,
): { listingType: ListingType; reason: string } {
  // Spec defaults — overrides for studio-specific shapes.
  if (
    productType === "Pre-built Deck" ||
    productType === "Trainer Staples Kit" ||
    productType === "Energy Kit"
  ) {
    return {
      listingType: "FixedPrice",
      reason: `Defaulted to Fixed Price — ${productType} is a repeatable product`,
    };
  }
  return pickDefaultListingType(cards, productType);
}

// ─── Wrap a BundleSuggestion as a ProductOpportunity ──────────────────────────

export function bundleSuggestionToOpportunity(
  suggestion: BundleSuggestion,
  options: { supplies?: ShippingSupply[] } = {},
): ProductOpportunity {
  const market = suggestion.estimatedMarketValue;
  const cost = suggestion.estimatedCostBasis;
  const cardCount = suggestion.cardCount;
  const fulfillment = estimateFulfillment({
    cards: suggestion.cards,
    productType: suggestion.suggestedProductType,
    supplies: options.supplies,
  });
  const economics = buildPricedEconomics({
    estimatedMarketValue: market,
    estimatedCostBasis: cost,
    productType: suggestion.suggestedProductType,
    fulfillmentCost: fulfillment.totalFulfillmentCost,
  });
  const buyerValueScore = computeBuyerValueScore(
    suggestion.cards,
    suggestion.suggestedProductType,
  );
  const listing = pickListingType(
    suggestion.cards,
    suggestion.suggestedProductType,
  );

  const warnings: string[] = [];
  if (economics.expectedProfit < 0) {
    warnings.push(
      `Expected profit is negative ($${economics.expectedProfit.toFixed(2)}).`,
    );
  }

  const reasons: string[] = [`${suggestion.label} — ${cardCount} cards.`];
  if (suggestion.estimatedMarketValue > 0) {
    reasons.push(
      `Estimated market value: $${suggestion.estimatedMarketValue.toFixed(2)}.`,
    );
  }

  return {
    id: suggestion.id,
    productType: suggestion.suggestedProductType,
    strategyId: suggestion.strategyId,
    title: suggestion.suggestedTitle,
    description: "",
    cards: suggestion.cards,
    cardIds: suggestion.cards.map((c) => c.id),
    cardCount,
    estimatedMarketValue: market,
    estimatedCostBasis: cost,
    expectedSalePrice: economics.expectedSalePrice,
    estimatedFulfillmentCost: fulfillment,
    expectedProfit: economics.expectedProfit,
    expectedMarginPct: economics.expectedMarginPct,
    buyerValueScore,
    confidenceScore: suggestion.confidenceScore,
    recommendedListingType: listing.listingType,
    listingTypeReason: listing.reason,
    reasons,
    warnings,
    targetCount: suggestion.targetCount,
    availabilityPct: suggestion.availabilityPct,
  };
}

// ─── Build a studio-strategy opportunity from raw cards ───────────────────────

function studioStrategyToOpportunity(
  strategyId: BundleStrategyId,
  productType: ProductType,
  cards: Card[],
  reasons: string[],
  targetCount: number,
  supplies: ShippingSupply[] | undefined,
): ProductOpportunity {
  const market = totalMarket(cards);
  const cost = totalCostBasis(cards);
  const fulfillment = estimateFulfillment({ cards, productType, supplies });
  const economics = buildPricedEconomics({
    estimatedMarketValue: market,
    estimatedCostBasis: cost,
    productType,
    fulfillmentCost: fulfillment.totalFulfillmentCost,
  });
  const buyerValueScore = computeBuyerValueScore(cards, productType);
  const listing = pickListingType(cards, productType);
  const cardCount = totalQty(cards);
  const warnings: string[] = [];
  if (economics.expectedProfit < 0) {
    warnings.push(
      `Expected profit is negative ($${economics.expectedProfit.toFixed(2)}).`,
    );
  }
  return {
    id: `studio-${strategyId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    productType,
    strategyId,
    title: `${productType} (${cardCount} cards)`,
    description: "",
    cards,
    cardIds: cards.map((c) => c.id),
    cardCount,
    estimatedMarketValue: market,
    estimatedCostBasis: cost,
    expectedSalePrice: economics.expectedSalePrice,
    estimatedFulfillmentCost: fulfillment,
    expectedProfit: economics.expectedProfit,
    expectedMarginPct: economics.expectedMarginPct,
    buyerValueScore,
    confidenceScore: Math.min(95, 50 + Math.floor(cardCount / 4)),
    recommendedListingType: listing.listingType,
    listingTypeReason: listing.reason,
    reasons,
    warnings,
    targetCount,
    availabilityPct: availabilityPctOf(cardCount, targetCount),
  };
}

// ─── Public: generate full opportunity list ───────────────────────────────────

export interface GenerateOpportunitiesInput {
  cards: Card[];
  /** Pre-existing BundleSuggestion list from generateBundles, or null to skip wrapping. */
  bundleSuggestions?: BundleSuggestion[];
  supplies?: ShippingSupply[];
}

export function generateProductOpportunities(
  input: GenerateOpportunitiesInput,
): ProductOpportunity[] {
  const out: ProductOpportunity[] = [];
  const usedIds = new Set<string>();

  // 1. Wrap each existing bundle suggestion (lot/theme/family etc.)
  for (const suggestion of input.bundleSuggestions ?? []) {
    out.push(
      bundleSuggestionToOpportunity(suggestion, { supplies: input.supplies }),
    );
    for (const c of suggestion.cards) usedIds.add(c.id);
  }

  // 2. Layer studio-only strategies on top using cards not yet claimed.
  for (const generator of STUDIO_STRATEGIES) {
    const result = generator(input.cards, usedIds);
    if (!result) continue;
    out.push(
      studioStrategyToOpportunity(
        result.strategyId,
        result.productType,
        result.cards,
        result.reasons,
        result.targetCount,
        input.supplies,
      ),
    );
    for (const c of result.cards) usedIds.add(c.id);
  }

  return out.sort((a, b) => b.expectedProfit - a.expectedProfit);
}
